import {
  ChatBubbleOvalLeftEllipsisIcon,
  ClockIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  MicrophoneIcon,
  PencilSquareIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

import {
  Listbox,
  ListboxLabel,
  ListboxOption,
} from '@/components/ui/catalyst/listbox';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { formatArtifactTypeForDisplay } from '../utils/artifactFormatters';

interface ArtifactSearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  typeFilter: ArtifactType | 'unprocessed' | 'all';
  onTypeFilterChange: (type: ArtifactType | 'unprocessed' | 'all') => void;
  availableTypes: ArtifactType[];
  placeholder?: string;
}

const getTypeIcon = (type: ArtifactType | 'unprocessed' | 'all') => {
  switch (type) {
    case ArtifactType.BLOB:
      return DocumentIcon;
    case ArtifactType.TRANSCRIPT:
      return MicrophoneIcon;
    case ArtifactType.CONVERSATION:
      return ChatBubbleOvalLeftEllipsisIcon;
    case ArtifactType.STAFF_NOTE:
      return PencilSquareIcon;
    case 'unprocessed':
      return ClockIcon;
    case 'all':
    default:
      return Squares2X2Icon;
  }
};

export const ArtifactSearchBar = ({
  searchQuery,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  availableTypes,
  placeholder = 'Search artifacts...',
}: ArtifactSearchBarProps) => {
  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
      {/* Search input */}
      <div className="relative flex-1">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-border/60 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-colors"
        />
      </div>

      {/* Type filter */}
      <div className="sm:w-48">
        <Listbox
          value={typeFilter}
          onChange={onTypeFilterChange}
          aria-label="Filter by artifact type"
        >
          <ListboxOption value="all">
            <Squares2X2Icon data-slot="icon" />
            <ListboxLabel>All Types</ListboxLabel>
          </ListboxOption>
          {availableTypes.map((type) => {
            const Icon = getTypeIcon(type);
            return (
              <ListboxOption key={type} value={type}>
                <Icon data-slot="icon" />
                <ListboxLabel>
                  {formatArtifactTypeForDisplay(type)}
                </ListboxLabel>
              </ListboxOption>
            );
          })}
          <ListboxOption value="unprocessed">
            <ClockIcon data-slot="icon" />
            <ListboxLabel>Unprocessed</ListboxLabel>
          </ListboxOption>
        </Listbox>
      </div>
    </div>
  );
};
